import { Component, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-pagination',
  imports: [CommonModule],
  template: `
    <nav [attr.aria-label]="'Pagination'" class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <button
          type="button"
          [disabled]="currentPage() === 1"
          (click)="goToPage(currentPage() - 1)"
          [class]="getButtonClasses('prev')"
          aria-label="Previous page">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        @for (page of visiblePages(); track page) {
          @if (page === '...') {
            <span class="px-2 text-gray-500 dark:text-gray-400">...</span>
          } @else {
            <button
              type="button"
              [class]="getPageButtonClasses(getPageNumber(page))"
              (click)="goToPage(getPageNumber(page))"
              [attr.aria-label]="'Go to page ' + page"
              [attr.aria-current]="getPageNumber(page) === currentPage() ? 'page' : null">
              {{ page }}
            </button>
          }
        }
        
        <button
          type="button"
          [disabled]="currentPage() === totalPages()"
          (click)="goToPage(currentPage() + 1)"
          [class]="getButtonClasses('next')"
          aria-label="Next page">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      
      @if (showInfo()) {
        <div class="text-sm text-gray-700 dark:text-gray-300">
          Showing {{ startItem() }} to {{ endItem() }} of {{ totalItems() }} results
        </div>
      }
    </nav>
  `,
  styles: []
})
export class PaginationComponent {
  currentPage = input<number>(1);
  totalItems = input<number>(0);
  itemsPerPage = input<number>(10);
  maxVisible = input<number>(5);
  showInfo = input<boolean>(false);
  size = input<ComponentSize>('md');

  pageChangeEvent = output<number>();

  totalPages = computed(() => Math.ceil(this.totalItems() / this.itemsPerPage()));
  
  startItem = computed(() => {
    return this.totalItems() === 0 ? 0 : (this.currentPage() - 1) * this.itemsPerPage() + 1;
  });
  
  endItem = computed(() => {
    return Math.min(this.currentPage() * this.itemsPerPage(), this.totalItems());
  });

  visiblePages(): (number | string)[] {
    const total = this.totalPages();
    const current = this.currentPage();
    const maxVisible = this.maxVisible();
    const pages: (number | string)[] = [];

    if (total <= maxVisible) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      if (current <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(total);
      } else if (current >= total - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = total - 3; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = current - 1; i <= current + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(total);
      }
    }

    return pages;
  }

  getPageNumber(page: number | string): number {
    return typeof page === 'number' ? page : 0;
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages() && page !== this.currentPage()) {
      this.pageChangeEvent.emit(page);
    }
  }

  getButtonClasses(type: 'prev' | 'next'): string {
    const sizeMap = {
      xs: 'p-1 text-xs',
      sm: 'p-1.5 text-sm',
      md: 'p-2 text-base',
      lg: 'p-2.5 text-lg',
      xl: 'p-3 text-xl'
    };

    return [
      'rounded-md',
      'border',
      'border-gray-300',
      'dark:border-gray-600',
      'bg-white',
      'dark:bg-gray-800',
      'text-gray-700',
      'dark:text-gray-300',
      'hover:bg-gray-50',
      'dark:hover:bg-gray-700',
      'disabled:opacity-50',
      'disabled:cursor-not-allowed',
      'transition-colors',
      'duration-200',
      sizeMap[this.size()]
    ].join(' ');
  }

  getPageButtonClasses(page: number): string {
    const sizeMap = {
      xs: 'px-2 py-1 text-xs min-w-[24px]',
      sm: 'px-2.5 py-1.5 text-sm min-w-[28px]',
      md: 'px-3 py-2 text-base min-w-[32px]',
      lg: 'px-4 py-2.5 text-lg min-w-[36px]',
      xl: 'px-5 py-3 text-xl min-w-[40px]'
    };

    const classes = [
      'rounded-md',
      'font-medium',
      'transition-colors',
      'duration-200',
      sizeMap[this.size()]
    ];

    if (page === this.currentPage()) {
      classes.push('bg-blue-600', 'text-white', 'dark:bg-blue-500');
    } else {
      classes.push('bg-white', 'dark:bg-gray-800', 'text-gray-700', 'dark:text-gray-300', 'hover:bg-gray-50', 'dark:hover:bg-gray-700', 'border', 'border-gray-300', 'dark:border-gray-600');
    }

    return classes.join(' ');
  }
}

